import React, { useEffect, useState, createContext, useContext, PropsWithChildren, useRef } from 'react';
import { AuthTokens, USER_AUTH_EVENT, USER_AUTH_KEY, USER_AUTH_TOKENS_EVENT, USER_AUTH_TOKENS_KEY, UserLogin } from './AuthClasses';
import { APIResponse } from '@cocoan/components/Classes';
import API, { CAPTCHA_ACTIVE, DEBUG, RECAPTCHA_KEY } from './AuthAPI';
import ReCAPTCHA from 'react-google-recaptcha';
import moment from 'moment';

const EXPIRED_CODES = [
	'P-TKNV', 'P-RFTKNV', 'P-TKNE',
]

var AuthContext = createContext<{
	user: UserLogin,
	user_saved: boolean,
	loginSaved: ()=>Promise<APIResponse<UserLogin>>,
	clearSaved: ()=>void,
	loggedIn: boolean,
	getCaptcha: ()=>Promise<string>,
}>(null);
var useAuth = () => useContext(AuthContext);
var AuthProvider = (props: PropsWithChildren)=>{
	var [user, setUser] = useState<UserLogin>(null);
	var [tokens, setTokens] = useState<AuthTokens>(null);
	var recaptchaRef = useRef<ReCAPTCHA>(null);

	useEffect(()=>{
		if(user===null) onLogin();
		window.addEventListener(USER_AUTH_EVENT, onLogin);
		window.addEventListener(USER_AUTH_TOKENS_EVENT, onTokens);
		return ()=>{
			window.removeEventListener(USER_AUTH_EVENT, onLogin);
			window.removeEventListener(USER_AUTH_TOKENS_EVENT, onTokens);
		}
	}, [user]);

	var onTokens = ()=>{
		var tokens = window.localStorage.getItem(USER_AUTH_TOKENS_KEY);
		if(DEBUG) console.log("[EVENT]: onTokens", tokens);
		try{
			var tokens_data = JSON.parse(tokens) as AuthTokens;
			var now = moment().unix();
			if(tokens_data?.expires<=(now+30)){
				window.localStorage.removeItem(USER_AUTH_TOKENS_KEY);
			}
			setTokens(tokens_data);
		}catch(e){
			console.log(e);
		}
	}

	var onLogin = ()=>{
		var login = window.localStorage.getItem(USER_AUTH_KEY);
		if(DEBUG) console.log("[EVENT]: onLogin", login);
		try{
			var login_data = JSON.parse(login) as UserLogin;
			var now = moment().unix();
			setUser(login_data);
			if(!login_data?.tokens || (login_data.tokens.refresh_expires !== null && login_data.tokens.refresh_expires<=(now+30))){
				window.localStorage.removeItem(USER_AUTH_KEY);
			}
		}catch(e){
			console.log(e);
		}
	}

	var loginSaved = () : Promise<APIResponse<UserLogin>>=>{
		if(!user || !user.tokens) return Promise.resolve({
			error: true,
			code: 'LCL-EXTSV-1',
			message: 'No existe una autenticación válida (LCL-EXTSV-1)'
		})
		return new Promise((resolve, reject)=>{
			API.loginRefresh(user.tokens.refresh).then(res=>{
				if(res.error && EXPIRED_CODES.indexOf(res.code)!==-1){
					clearSaved();
				}
				return resolve(res);
			}).catch(reject);
		})
	}

	var clearSaved = ()=>{
		window.localStorage.removeItem(USER_AUTH_KEY);
		window.localStorage.removeItem(USER_AUTH_TOKENS_KEY);
		setUser(null);
		setTokens(null);
	}

	var getCaptcha = async ()=>{
		if(!CAPTCHA_ACTIVE) return 'INACTIVE_CAPTCHA';
		if(!recaptchaRef.current) return null;
		recaptchaRef.current?.reset();
		return recaptchaRef.current?.executeAsync()
	}
	
	var now = moment().unix();
	
	return <AuthContext.Provider value={{
		user,
		user_saved: user && user.tokens?.refresh && (!user.tokens.refresh_expires || user.tokens.refresh_expires>(now+30)),
		loginSaved,
		clearSaved,
		loggedIn: !!tokens,
		getCaptcha,
	}}>
		{props.children}
		{CAPTCHA_ACTIVE && (
			<ReCAPTCHA 
				hl='es-419'
				size='invisible'
				sitekey={RECAPTCHA_KEY}
				ref={recaptchaRef}
			/>
		)}
	</AuthContext.Provider>
}

export {
	useAuth,
	AuthProvider,
}